// src/components/timeline/TimelineReactionBar.tsx

"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { PostReactionSummary, ReactionCounts } from "@/lib/timeline";
import { toggleTimelineReactionAction } from "@/app/[locale]/timeline/actions";
import { useAnonymousUser } from "@/hooks/useAnonymousUser";
import { useLocale } from "next-intl";

type Props = {
  projectId: number;
  postId: string;
  initialSummary?: PostReactionSummary | null;
};

type ReactionKey = "like" | "love" | "wow";

type ReactionItem = {
  key: ReactionKey;
  label: string;
  emoji: string;
  value: 2 | 3 | 4;
  aria: string;
};

const REACTIONS: ReactionItem[] = [
  {
    key: "like",
    label: "Like",
    emoji: "👍",
    value: 2,
    aria: "React with Like",
  },
  {
    key: "love",
    label: "Love",
    emoji: "❤️",
    value: 3,
    aria: "React with Love",
  },
  {
    key: "wow",
    label: "Wow",
    emoji: "😮",
    value: 4,
    aria: "React with Wow",
  },
];

function emptyCounts(): ReactionCounts {
  return {
    view: 0,
    like: 0,
    love: 0,
    wow: 0,
  };
}

// function formatCompactNumber(value: number) {
//   try {
//     return new Intl.NumberFormat(undefined, {
//       notation: "compact",
//       maximumFractionDigits: 1,
//     }).format(value);
//   } catch {
//     if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
//     if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
//     return String(value);
//   }
// }

function getTotal(counts?: ReactionCounts | null) {
  if (!counts) return 0;

  return (
    Number(counts.like || 0) +
    Number(counts.love || 0) +
    Number(counts.wow || 0)
  );
}

function applyOptimisticToggle(
  current: PostReactionSummary | null,
  postId: string,
  reaction: ReactionItem,
): PostReactionSummary {
  const currentCounts = current?.counts ?? emptyCounts();
  const currentUserReactions = current?.user_reactions ?? [];
  const alreadyActive = currentUserReactions.includes(reaction.key);

  const nextCounts: ReactionCounts = { ...currentCounts };
  let nextUserReactions: string[];

  if (alreadyActive) {
    nextCounts[reaction.key] = Math.max(0, nextCounts[reaction.key] - 1);
    nextUserReactions = currentUserReactions.filter(
      (key) => key !== reaction.key,
    );
  } else {
    nextCounts[reaction.key] = nextCounts[reaction.key] + 1;
    nextUserReactions = [
      ...currentUserReactions.filter((key) => key !== "view"),
      reaction.key,
    ];
  }

  return {
    post_id: current?.post_id ?? postId,
    counts: nextCounts,
    user_reactions: nextUserReactions,
  };
}

export default function TimelineReactionBar({
  projectId,
  postId,
  initialSummary = null,
}: Props) {
  const { userId: anonymousUserId } = useAnonymousUser(); // Remove isReady

  const [summary, setSummary] = useState<PostReactionSummary | null>(
    initialSummary,
  );
  const [loading, setLoading] = useState(!initialSummary);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busyReaction, setBusyReaction] = useState<ReactionKey | null>(null);
  const [statusMessage, setStatusMessage] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(0);

  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const trayRef = useRef<HTMLDivElement | null>(null);

  const locale = useLocale();

  function formatCompactNumber(value: number) {
    return new Intl.NumberFormat(locale, {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  }

  useEffect(() => {
    if (initialSummary) {
      setSummary(initialSummary);
      setLoading(false);
    }
  }, [initialSummary]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!pickerOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPickerOpen(false);
        triggerRef.current?.focus();
      }

      if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        event.preventDefault();
        setFocusedIndex((index) => {
          const next = (index + 1) % REACTIONS.length;
          const button = trayRef.current?.querySelectorAll<HTMLButtonElement>(
            "[data-reaction-item]",
          )[next];
          button?.focus();
          return next;
        });
      }

      if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        event.preventDefault();
        setFocusedIndex((index) => {
          const next = (index - 1 + REACTIONS.length) % REACTIONS.length;
          const button = trayRef.current?.querySelectorAll<HTMLButtonElement>(
            "[data-reaction-item]",
          )[next];
          button?.focus();
          return next;
        });
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [pickerOpen]);

  const userReactions = useMemo(
    () => new Set(summary?.user_reactions ?? []),
    [summary?.user_reactions],
  );

  const activePills = useMemo(() => {
    if (!summary) return [];

    return REACTIONS.filter((reaction) => {
      const count = summary.counts[reaction.key] ?? 0;
      return count > 0 || userReactions.has(reaction.key);
    }).sort((a, b) => {
      const aActive = userReactions.has(a.key) ? 1 : 0;
      const bActive = userReactions.has(b.key) ? 1 : 0;

      if (aActive !== bActive) return bActive - aActive;

      return (summary.counts[b.key] ?? 0) - (summary.counts[a.key] ?? 0);
    });
  }, [summary, userReactions]);

  const primaryReaction = REACTIONS[0];
  const totalCount = getTotal(summary?.counts);

  async function onToggle(reaction: ReactionItem) {
    if (busyReaction) return;

    // Ensure we have a user ID for the action
    if (!anonymousUserId) {
      setStatusMessage("Unable to save reaction. Please refresh the page.");
      return;
    }

    const previous = summary;
    const optimistic = applyOptimisticToggle(summary, postId, reaction);

    setSummary(optimistic);
    setBusyReaction(reaction.key);
    setStatusMessage(
      userReactions.has(reaction.key)
        ? `${reaction.label} removed`
        : `${reaction.label} added`,
    );

    try {
      const res = await toggleTimelineReactionAction(
        projectId,
        postId,
        reaction.value,
        anonymousUserId, // Pass the ID directly
      );

      setSummary({
        post_id: res.post_id,
        counts: res.counts,
        user_reactions: res.user_reactions,
      });
    } catch (error) {
      console.error("Failed to toggle reaction:", error);
      setSummary(previous);
      setStatusMessage("Reaction not saved. Try again.");
    } finally {
      setBusyReaction(null);
      setPickerOpen(false);

      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      closeTimerRef.current = setTimeout(() => {
        setStatusMessage("");
      }, 2200);
    }
  }

  function openPicker() {
    setPickerOpen(true);
    setFocusedIndex(0);
  }

  function closePickerSoon() {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setPickerOpen(false);
    }, 160);
  }

  function cancelClosePicker() {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }

  function startLongPress() {
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);

    longPressTimerRef.current = setTimeout(() => {
      openPicker();
    }, 360);
  }

  function cancelLongPress() {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }

  // Simplified loading check - no isReady needed
  if (loading && !summary) {
    return (
      <div className="timeline-reactions__loading" aria-live="polite">
        <span className="timeline-reactions__skeleton" />
        <span>Loading reactions...</span>
      </div>
    );
  }

  return (
    <div className="timeline-reactions" aria-label="Post reactions">
      <div className="timeline-reactions__cluster">
        {activePills.length === 0 ? (
          <button
            type="button"
            className="timeline-reaction-pill"
            onClick={() => onToggle(primaryReaction)}
            onPointerDown={startLongPress}
            onPointerUp={cancelLongPress}
            onPointerLeave={cancelLongPress}
            disabled={busyReaction !== null}
            aria-label={primaryReaction.aria}
            title={primaryReaction.label}
          >
            <span className="timeline-reaction-pill__emoji" aria-hidden="true">
              {primaryReaction.emoji}
            </span>
            <span>{primaryReaction.label}</span>
          </button>
        ) : (
          activePills.slice(0, 3).map((reaction) => {
            const isActive = userReactions.has(reaction.key);
            const count = summary?.counts[reaction.key] ?? 0;

            return (
              <button
                key={reaction.key}
                type="button"
                className={[
                  "timeline-reaction-pill",
                  isActive ? "is-active" : "",
                  busyReaction === reaction.key ? "is-busy" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => onToggle(reaction)}
                disabled={busyReaction !== null}
                aria-pressed={isActive}
                aria-label={
                  isActive ? `Remove ${reaction.label} reaction` : reaction.aria
                }
                title={reaction.label}
              >
                <span
                  className="timeline-reaction-pill__emoji"
                  aria-hidden="true"
                >
                  {reaction.emoji}
                </span>
                <span className="timeline-reaction-pill__count">
                  {formatCompactNumber(count)}
                </span>
              </button>
            );
          })
        )}

        {totalCount > 0 && activePills.length > 3 && (
          <span className="timeline-reaction-total" title={`${totalCount}`}>
            +{formatCompactNumber(totalCount)}
          </span>
        )}
      </div>

      <div
        className="timeline-reaction-picker"
        onMouseEnter={cancelClosePicker}
        onMouseLeave={closePickerSoon}
      >
        <button
          ref={triggerRef}
          type="button"
          className={["timeline-reaction-trigger", pickerOpen ? "is-open" : ""]
            .filter(Boolean)
            .join(" ")}
          onClick={() => setPickerOpen((value) => !value)}
          onMouseEnter={openPicker}
          aria-label="Choose a reaction"
          aria-haspopup="menu"
          aria-expanded={pickerOpen}
          title="Choose a reaction"
        >
          <span className="timeline-reaction-trigger__icon" aria-hidden="true">
            🙂
          </span>
        </button>

        {pickerOpen && (
          <>
            <button
              type="button"
              className="timeline-reaction-backdrop"
              aria-label="Close reaction picker"
              onClick={() => setPickerOpen(false)}
              tabIndex={-1}
            />

            <div
              ref={trayRef}
              className="timeline-reaction-tray"
              role="menu"
              aria-label="Reaction options"
            >
              {REACTIONS.map((reaction, index) => {
                const isSelected = userReactions.has(reaction.key);

                return (
                  <button
                    key={reaction.key}
                    type="button"
                    data-reaction-item
                    role="menuitemradio"
                    aria-checked={isSelected}
                    className={[
                      "timeline-reaction-tray__item",
                      isSelected ? "is-selected" : "",
                      busyReaction !== null ? "is-busy" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    disabled={busyReaction !== null}
                    onClick={() => onToggle(reaction)}
                    onFocus={() => setFocusedIndex(index)}
                    title={reaction.label}
                    aria-label={
                      isSelected
                        ? `Remove ${reaction.label} reaction`
                        : reaction.aria
                    }
                  >
                    <span aria-hidden="true">{reaction.emoji}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      <span
        className="timeline-reactions__status"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {statusMessage}
      </span>
    </div>
  );
}
